import ObjectToURLUtil from "@/utils/urlUtils/ObjectToURLUtil.ts";
import BarCodeConstants from "@/utils/constants/BarCodeConstants.ts";
import ListViewQuickFilterButtonVal = namaTypes.ListViewQuickFilterButtonVal;
import ListViewQuickFilterSelectedValues = namaTypes.ListViewQuickFilterSelectedValues;
import {URLSerializer} from "@/utils/presenters/interfaces.ts";
import BasicFieldMetaData = namaTypes.BasicFieldMetaData;
import GenericValue = namaTypes.GenericValue;

const CRITERIA_ID = "cid";
const CRITERIA_TEXT = "ctx";
const FIELD_ID = "fid";
const VALUE = "vlu";
const DATA_TYPE = "dtp";
const EMPTY = "emp";
const TITLE_AR = "tar";
const TITLE_EN = "ten";
const COUNT = "cnt";
const META_FIELD_ID = "mfi";
const META_FIELD_TYPE = "mft";
const META_ENUM_TYPE = "met";
const META_REF_ENTITY = "mre";
const REF_ID = "rid";
const REF_TYPE = "rtp";
const REF_CODE = "rcd";
const BOOL_VAL = "bvl";
const INT_VAL = "ivl";
const LONG_VAL = "lvl";
const DEC_VAL = "dvl";

export default class QuickFilterURLSerializer implements URLSerializer<ListViewQuickFilterButtonVal> {

  flattenIfNeeded(buttons?: ListViewQuickFilterButtonVal[]) {
    return buttons;
  }

  toProperties(button: ListViewQuickFilterButtonVal): Map<string, string> {
    const map: Map<string, string> = new Map();
    map.set(CRITERIA_ID, button.criteriaId ?? "");
    map.set(CRITERIA_TEXT, button.criteriaText ?? "");
    map.set(COUNT, button.count != null ? String(button.count) : "");
    map.set(TITLE_AR, button.title?.arabicText ?? "");
    map.set(TITLE_EN, button.title?.englishText ?? "");

    if (button.columnValues?.length) {
      map.set(FIELD_ID, button.columnValues.map(cv => cv.fieldId ?? "").join(BarCodeConstants.listSeperator));
      map.set(VALUE, button.columnValues.map(cv => cv.textData ?? "").join(BarCodeConstants.listSeperator));
      map.set(DATA_TYPE, button.columnValues.map(cv => cv.dataType ?? "").join(BarCodeConstants.listSeperator));
      map.set(EMPTY, button.columnValues.map(cv => cv.empty ? "1" : "0").join(BarCodeConstants.listSeperator));
      map.set(REF_ID, button.columnValues.map(cv => cv.entityReferenceData?.id ?? "").join(BarCodeConstants.listSeperator));
      map.set(REF_TYPE, button.columnValues.map(cv => cv.entityReferenceData?.entityType ?? "").join(BarCodeConstants.listSeperator));
      map.set(REF_CODE, button.columnValues.map(cv => cv.entityReferenceData?.code ?? "").join(BarCodeConstants.listSeperator));
      map.set(BOOL_VAL, button.columnValues.map(cv => cv.booleanData != null ? String(cv.booleanData) : "").join(BarCodeConstants.listSeperator));
      map.set(INT_VAL, button.columnValues.map(cv => cv.integerData != null ? String(cv.integerData) : "").join(BarCodeConstants.listSeperator));
      map.set(LONG_VAL, button.columnValues.map(cv => cv.longData != null ? String(cv.longData) : "").join(BarCodeConstants.listSeperator));
      map.set(DEC_VAL, button.columnValues.map(cv => cv.bigDecimalData != null ? String(cv.bigDecimalData) : "").join(BarCodeConstants.listSeperator));
    }

    if (button.columnMetadata?.length) {
      map.set(META_FIELD_ID, button.columnMetadata.map(m => m.fieldId ?? "").join(BarCodeConstants.listSeperator));
      map.set(META_FIELD_TYPE, button.columnMetadata.map(m => m.fieldType ?? "").join(BarCodeConstants.listSeperator));
      map.set(META_ENUM_TYPE, button.columnMetadata.map(m => m.enumTypeName ?? "").join(BarCodeConstants.listSeperator));
      map.set(META_REF_ENTITY, button.columnMetadata.map(m => m.referencedEntityType ?? "").join(BarCodeConstants.listSeperator));
    }

    return map;
  }

  fromProperties(properties: Map<string, string>): ListViewQuickFilterButtonVal {
    const button: ListViewQuickFilterButtonVal = {};
    button.criteriaId = properties[CRITERIA_ID] || undefined;
    button.criteriaText = properties[CRITERIA_TEXT] || undefined;

    const countStr = properties[COUNT];
    if (countStr) button.count = Number(countStr);

    const titleAr = properties[TITLE_AR];
    const titleEn = properties[TITLE_EN];
    if (titleAr || titleEn)
      button.title = {arabicText: titleAr || undefined, englishText: titleEn || undefined};

    const fieldIds = properties[FIELD_ID];
    if (fieldIds) {
      const fids = fieldIds.split(BarCodeConstants.listSeperator);
      const vals = this.splitOrEmpty(properties[VALUE], fids.length);
      const dataTypes = this.splitOrEmpty(properties[DATA_TYPE], fids.length);
      const empties = this.splitOrEmpty(properties[EMPTY], fids.length);
      const refIds = this.splitOrEmpty(properties[REF_ID], fids.length);
      const refTypes = this.splitOrEmpty(properties[REF_TYPE], fids.length);
      const refCodes = this.splitOrEmpty(properties[REF_CODE], fids.length);
      const bools = this.splitOrEmpty(properties[BOOL_VAL], fids.length);
      const ints = this.splitOrEmpty(properties[INT_VAL], fids.length);
      const longs = this.splitOrEmpty(properties[LONG_VAL], fids.length);
      const decs = this.splitOrEmpty(properties[DEC_VAL], fids.length);

      button.columnValues = fids.map((fid, i): GenericValue => {
        const cv: GenericValue = {fieldId: fid};
        if (vals[i]) cv.textData = vals[i];
        if (dataTypes[i]) cv.dataType = dataTypes[i];
        if (empties[i] === "1") cv.empty = true;
        if (refIds[i])
          cv.entityReferenceData = {id: refIds[i], entityType: refTypes[i] || undefined, code: refCodes[i] || undefined};
        if (bools[i]) cv.booleanData = bools[i] === "true";
        if (ints[i]) cv.integerData = Number(ints[i]);
        if (longs[i]) cv.longData = Number(longs[i]);
        if (decs[i]) cv.bigDecimalData = Number(decs[i]);
        return cv;
      });
    }

    const metaFieldIds = properties[META_FIELD_ID];
    if (metaFieldIds) {
      const mfids = metaFieldIds.split(BarCodeConstants.listSeperator);
      const mftypes = this.splitOrEmpty(properties[META_FIELD_TYPE], mfids.length);
      const menums = this.splitOrEmpty(properties[META_ENUM_TYPE], mfids.length);
      const mrefs = this.splitOrEmpty(properties[META_REF_ENTITY], mfids.length);

      button.columnMetadata = mfids.map((mfid, i): BasicFieldMetaData => ({
        fieldId: mfid || undefined,
        fieldType: mftypes[i] || undefined,
        enumTypeName: menums[i] || undefined,
        referencedEntityType: mrefs[i] || undefined,
      }));
    }

    return button;
  }

  private splitOrEmpty(value: string | undefined, expectedLength: number): string[] {
    if (!value)
      return new Array(expectedLength).fill("");
    return value.split(BarCodeConstants.listSeperator);
  }

  toURL(selectedGroups: ListViewQuickFilterSelectedValues[]): string {
    const allButtons: ListViewQuickFilterButtonVal[] = [];
    for (const group of selectedGroups) {
      if (group.selectedValues?.length)
        allButtons.push(...group.selectedValues);
    }
    if (!allButtons.length) return "";
    return ObjectToURLUtil.toUrl(allButtons, this as any);
  }

  fromURL(url: string | undefined): ListViewQuickFilterButtonVal[] {
    if (!url) return [];
    return ObjectToURLUtil.fromURL(url, this as any) ?? [];
  }

  matchesButton(serialized: ListViewQuickFilterButtonVal, actual: ListViewQuickFilterButtonVal): boolean {
    if (serialized.criteriaId && actual.criteriaId)
      return serialized.criteriaId === actual.criteriaId;
    if (!serialized.columnValues?.length || !actual.columnValues?.length)
      return false;
    if (serialized.columnValues.length !== actual.columnValues.length)
      return false;
    for (let i = 0; i < serialized.columnValues.length; i++) {
      if (serialized.columnValues[i].fieldId !== actual.columnValues[i].fieldId)
        return false;
      if (this.extractValue(serialized.columnValues[i]) !== this.extractValue(actual.columnValues[i]))
        return false;
    }
    return true;
  }

  private extractValue(cv: GenericValue): string {
    if (cv.empty) return "__empty__";
    if (cv.textData != null) return cv.textData;
    if (cv.entityReferenceData?.id) return cv.entityReferenceData.id;
    if (cv.integerData != null) return String(cv.integerData);
    if (cv.longData != null) return String(cv.longData);
    if (cv.bigDecimalData != null) return String(cv.bigDecimalData);
    if (cv.booleanData != null) return String(cv.booleanData);
    if (cv.dateData) return String(cv.dateData);
    return "";
  }
}
